'use client'

import { motion } from 'framer-motion'
import { DollarSign, ShoppingCart, TrendingUp, PieChart, Activity } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'

const metrics = [
  { label: 'Revenue Generated', value: '$24,850', icon: DollarSign, change: '+18%' },
  { label: 'Conversions Assisted', value: '1,247', icon: ShoppingCart, change: '+24%' },
  { label: 'AOV Uplift', value: '+16%', icon: TrendingUp, change: '+9%' },
  { label: 'Net Revenue Gain vs Discounts', value: '$6,120', icon: PieChart, change: '+12%' },
]

export function RevenueAttributionSection() {
  return (
    <Section background="dark" className="relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-violet/5 rounded-full blur-3xl pointer-events-none" />

      <Container className="relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-micro-uppercase text-brand-violet mb-3">REVENUE ATTRIBUTION</p>
          <h2 className="text-display-1 text-ink mb-4">
            See Exactly What the AI Delivers
          </h2>
          <p className="text-body-lg text-muted max-w-2xl mx-auto">
            Every assisted conversion, every negotiated sale, every AOV uplift — clearly attributed. No black box.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {/* Dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 rounded-2xl bg-panel border border-line p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-brand-violet" />
                <span className="font-semibold text-ink">AI Performance Dashboard</span>
              </div>
              <span className="text-xs text-muted">Illustrative dashboard</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {metrics.map((metric, index) => {
                const Icon = metric.icon
                return (
                  <motion.div
                    key={metric.label}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-4 rounded-xl bg-page border border-line hover:border-brand-violet-line transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Icon className="w-5 h-5 text-brand-violet" />
                      <span className="text-xs font-medium text-brand-green">{metric.change}</span>
                    </div>
                    <div className="text-2xl font-bold text-ink mb-1">{metric.value}</div>
                    <div className="text-xs text-muted">{metric.label}</div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>

          {/* Side benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-2xl bg-brand-violet-soft border border-brand-violet-line p-6 flex flex-col justify-center"
          >
            <h3 className="text-xl font-semibold text-brand-violet mb-4">Clear ROI</h3>
            <ul className="space-y-3">
              {[
                'Track every AI-assisted sale',
                'Measure discount ROI vs margin',
                'See which agents drive revenue',
                'Export reports for your team',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-ink text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-violet" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </Container>
    </Section>
  )
}
